import React from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import EnquireModal from "../components/EnquireModal";
import Lightbox from "../components/Lightbox";
import "../styles/pages.css";
import "../styles/enquire.css";

export default function About() {
  const [enquire, setEnquire] = React.useState(false);
  const [lb, setLb] = React.useState(null); // { images, index }
  const openLb = (images, index = 0) => setLb({ images, index });
  const closeLb = () => setLb(null);
  const navLb = (d) => setLb((s) => s ? { ...s, index: (s.index + d + s.images.length) % s.images.length } : s);
  React.useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  return (
    <div className="kmr-page about-pg" data-testid="about-page">
      <Nav />

      <section className="about-hero">
        <div className="about-hero-left">
          <div className="hero-eyebrow">Dubai · Working Globally · Est. 2018</div>
          <h1>Komorebi.<br /><em>A studio<br />with a<br />conscience.</em></h1>
          <div className="titles-strip">
            {["Strategy","Branding","Behaviour Change","Citizen Science","Sustainability","Education","Filmmaking"].map((t) => (
              <span key={t} className="title-tag">{t}</span>
            ))}
          </div>
          <p className="hero-tagline">"Where beauty meets purpose, the planet breathes."</p>
        </div>
        <div className="about-hero-right">
          <img src="/images/ishita.jpg" alt="Ishita Das, Founder of Komorebi Design Studio" onClick={() => openLb(["/images/ishita.jpg"], 0)} style={{ cursor: "zoom-in" }} data-testid="about-ishita-img" />
          <div className="photo-caption">Ishita Das. Sitting under The Great Wave. Of course.</div>
        </div>
      </section>

      <section className="story-section">
        <div className="story-left">
          <div className="section-label">The Studio</div>
          <h2>Design at the intersection of strategy, creativity, and systems change.</h2>
          <p>Komorebi is a multidisciplinary design studio named after the Japanese word for sunlight filtering through leaves. We work where branding meets behaviour change, where conservation storytelling meets citizen science, where a door hanger can become a manifesto.</p>
          <p>We are based in Dubai and work globally — with conscious brands, NGOs, social enterprises, governments, and anyone who needs someone to connect the dots between purpose and impact.</p>
          <div className="pull-quote">"Design done with consciousness can change the world."</div>
        </div>
        <div className="story-right">
          <p>Every project we take on moves through our Earth Consciousness™ framework: <strong>Break</strong> the existing pattern. <strong>Seed</strong> new awareness. <strong>Rewire</strong> the behaviour. <strong>Repeat</strong> until it's systemic.</p>
          <p>We have designed brand identities for ecology studios and Pakistani immigrant laundries. We have produced the first citizen science foraminifera study in the UAE. We have told the story of a Kolkata cemetery transforming into a biodiversity park over 15 years. We have written children's books about Arabian dugongs, designed living billboards for 60,000 pollinators, and restored 19th-century terracotta temples.</p>
          <p>We do not work with greenwashers, corporate ladder-climbers, or clients who want decoration instead of transformation. <strong>We work with people who are trying to build something that matters.</strong></p>
        </div>
      </section>

      <div className="awakening">
        <blockquote>"I genuinely believe design can heal the planet if we create with purpose, intention and love. Nature is our ally in the matter."</blockquote>
        <cite>— Ishita Das, Founder</cite>
      </div>

      <section className="principles">
        <div className="principles-inner">
          <div className="section-label">Soul Principles</div>
          <h2>The rules Komorebi lives by — <em>and designs from.</em></h2>
          <div className="principles-list">
            {[
              "The main thing is to keep the main thing, the main thing.",
              "Don't be afraid of your power.",
              "The goal is not to win. It is to perpetuate the game.",
              "Design, done with consciousness, can change the world.",
            ].map((p, i) => (
              <div key={p} className="principle-item">
                <div className="principle-num">{`0${i+1}`}</div>
                <p>{p}</p>
              </div>
            ))}
            <div className="principle-item" style={{ gridColumn: "1/-1" }}>
              <div className="principle-num">05</div>
              <p>The forest is gentle. The forest is unstoppable. Same thing.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="framework">
        <div className="framework-inner">
          <div className="section-label">Earth Consciousness™ Framework</div>
          <h2>How Komorebi <em>changes systems.</em></h2>
          <div className="fw-grid">
            {[
              ["01","Break","Interrupt","The circular economy begins with interruption — not recycling. We find the pattern and disrupt it."],
              ["02","Seed","Plant","Consciousness doesn't arrive fully formed. We plant it — in a child's book, a bee station, a door hanger."],
              ["03","Rewire","Change","New behaviours need new infrastructure. We design the habit, not just the message."],
              ["04","Repeat","Systemise","The goal is not to win. It is to perpetuate the game. We build for permanence."],
            ].map(([n,name,t,d]) => (
              <div key={n} className="fw-step">
                <div className="fw-num">{n}</div>
                <div className="fw-name">{name}</div>
                <div className="fw-title">{t}</div>
                <div className="fw-desc">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="circularity-section">
        <div className="circularity-inner">
          <div className="section-label">Circularity Design Framework</div>
          <h2>How to build a <em>circular economy.</em></h2>
          <p className="intro">The Earth Consciousness™ framework tells us why. The Circularity Design Framework tells us how — a four-step loop that interrupts linear systems and replaces them with regenerative ones. Every Komorebi project moves through this cycle.</p>
          <div className="circularity-img-wrap">
            <img src="/images/Komorebi_FRAMEWORK_POST_Template_ID.png" alt="Komorebi Circularity Design Framework" loading="lazy" onClick={() => openLb(["/images/Komorebi_FRAMEWORK_POST_Template_ID.png"], 0)} style={{ cursor: "zoom-in" }} data-testid="about-framework-img" />
            <div className="circularity-steps">
              {[
                ["Step 01","Break the Linear Cycle","Interrupt and disrupt the current system. Nothing changes until the existing pattern is stopped."],
                ["Step 02","Insert a Consciousness Lever","Add a sustainability principle — the 7 R's, Earth Consciousness™, a CSR or ESG mandate. Give the disruption a direction."],
                ["Step 03","Create New Connections","Rewire the system. Make new connections. Form new networks — like neuron pathways. Design the infrastructure for the new behaviour."],
                ["Step 04","Form a Circular System","Generate a regenerative, sustainable loop. The goal is not to win. It is to perpetuate the game."],
              ].map(([n,t,d]) => (
                <div key={n} className="circ-step">
                  <div className="circ-num">{n}</div>
                  <div className="circ-title">{t}</div>
                  <div className="circ-desc">{d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="team-section">
        <div className="team-inner">
          <div className="section-label">The Founder</div>
          <h2>The human <em>behind the studio.</em></h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: "var(--paper3)", marginBottom: 48 }}>
            <div className="team-item" style={{ gridColumn: "1/-1", padding: "48px 44px" }}>
              <h3 style={{ fontSize: "1.4rem", marginBottom: 8 }}>Ishita Das</h3>
              <div className="role" style={{ marginBottom: 20 }}>Founder · Chief Design Officer · Renaissance Designer · Warrior of the Light</div>
              <p style={{ marginBottom: 16 }}>She was ten years old when she read an infographic: plastic takes 500 years to decompose. That child is still running Komorebi. Trained in Pune, Ahmedabad, and New York (NYFA, Film Direction), Ishita has worked at Ogilvy, taught at universities across three countries, documented cyclone survivors in the Sundarbans, and designed for human rights organisations. She is a designer, filmmaker, educator, conservationist, and citizen scientist — all at once, all the time.</p>
              <p>Based in Dubai since 2018. Working globally. Building a life that becomes a legacy.</p>
            </div>
          </div>
          <div className="section-label" style={{ marginBottom: "1.5rem" }}>The People Who Hold the Mission Steady</div>
          <div className="team-grid">
            {[
              ["Alan George","Business Architect · Life Partner","Helping Komorebi make the tough choices with clarity and courage."],
              ["Tania Das","Ecology & Conservation · Founder, Forêt","Landscape designer, conservationist, artist, and founder of Forêt. The muse and the mirror."],
              ["Jovell Godoy","Steward of Flow","Keeping Komorebi's rhythm steady — the operations heartbeat behind every project that ships."],
              ["Damini Agarwal","Brand Maker","Translating Komorebi's voice into visual systems. The calm hand on every identity."],
              ["Dr. Neeta Das","Systems Leader · NSDA · CHF & KSHT Collaborator","Showing what it means to do good work at scale, with full integrity."],
              ["Erfan Firouzi","Biodiversity Keeper · Wildlife Focus","The science anchor at every citizen science event."],
            ].map(([n,r,d]) => (
              <div key={n} className="team-item">
                <h3>{n}</h3>
                <div className="role">{r}</div>
                <p>{d}</p>
              </div>
            ))}
          </div>

          <div className="section-label" style={{ marginTop: "3.5rem", marginBottom: "1.25rem" }}>Apprentices — past, present, future</div>
          <div className="apprentices-block" data-testid="apprentices-block">
            <p className="apprentices-names">
              {[
                "Harishri Upadhayay",
                "Alviya Saidi",
                "Fahad Showkath",
                "Tahreem Awais",
                "Chanchal Bar",
                "Bijoy Das",
                "I Gusti Ayu Emi Avrilia",
                "Jeet Das",
              ].map((name, i, arr) => (
                <React.Fragment key={name}>
                  <span className="apprentice-name">{name}</span>
                  {i < arr.length - 1 && <span className="apprentice-sep" aria-hidden="true">·</span>}
                </React.Fragment>
              ))}
            </p>
            <p className="apprentices-note">
              The Komorebi apprenticeship is not a programme. <em>It is a relationship.</em> Every person who has passed through this studio has left something behind — and taken something forward.
            </p>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <h2>"The first conversation is always free."</h2>
        <p>We work with conscious brands, NGOs, social enterprises, and governments who need someone to connect the dots between purpose and impact.</p>
        <a href="mailto:hey@komorebidesign.com" className="cta-email" data-testid="about-cta-email">hey@komorebidesign.com</a>
        <div style={{ marginTop: "1.5rem" }}>
          <button className="btn-outline" onClick={() => setEnquire(true)} data-testid="about-cta-modal" style={{ borderColor: "rgba(255,255,255,0.6)", color: "#fff" }}>Send a note instead</button>
        </div>
      </section>

      <Footer />
      {lb && <Lightbox images={lb.images} index={lb.index} onClose={closeLb} onNav={navLb} />}
      <EnquireModal open={enquire} onClose={() => setEnquire(false)} item="A first conversation" kind="contact" />
    </div>
  );
}
